import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { MEMBERSHIP, WEB_CURRENCIES, type WalletView, type WebCatalogItem, type WebCurrency } from '@lc/contracts';
import { api } from '../api';
import { CURRENCY_LABEL, formatPrice } from '../format';
import { useI18n, type Key } from '../i18n';
import { useLoad, useSession } from '../session';
import { CreditIcon, LangToggle, Plate, Wordmark } from '../ui/chrome';
import { BagGlyph, BookGlyph, BoltGlyph, BrushGlyph, CheckGlyph, HomeGlyph, LockGlyph, MicGlyph, ShirtGlyph, UserGlyph } from '../ui/glyphs';

const CURRENCY_KEY = 'inked.web.currency';
const savedCurrency = (): WebCurrency | null => {
  try {
    const v = localStorage.getItem(CURRENCY_KEY);
    return (WEB_CURRENCIES as readonly string[]).includes(v ?? '') ? (v as WebCurrency) : null;
  } catch {
    return null;
  }
};

/** Pack illustrations: ink coins growing with the pack. */
const PACK_ART: Record<string, string> = { chapter_1: 'coin_1', chapter_3: 'coin_3', chapter_10: 'coin_6', month_card: 'coin_3' };
/** The app opens the shop on the product the player tapped (?product=…): it's scrolled to and marked. */
const PRODUCT_ID = /^[a-z0-9_]{1,40}$/;
const anchorOf = (productId: string) => `product-${productId}`;

/** Membership perks, in catalogue order: tickets, daily coins, chats and chapters, energy, the outfit and cancelling. */
const PERK_ICONS: ReactNode[] = [<BrushGlyph key="c" size={16} />, <BookGlyph key="b" size={16} />, <MicGlyph key="m" size={16} />, <BoltGlyph key="e" size={16} />, <ShirtGlyph key="s" size={16} />];

/** 墨店 (Store v2): the memberships, the 月卡 and chapter tickets; prices from Stripe in the chosen currency. */
export function ShopPage() {
  const { t, lang } = useI18n();
  const session = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [currency, setCurrency] = useState<WebCurrency | null>(savedCurrency);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The pre-registration gift is spent on the next purchase unless the buyer unticks it.
  const [useGift, setUseGift] = useState(true);
  const catalog = useLoad(() => api.catalog(currency), [currency, lang, session?.userId]);
  const wallet = useLoad(() => (session ? api.wallet() : Promise.resolve(null)), [session?.userId]);
  const cur = catalog.data?.currency ?? currency ?? 'hkd';

  const choose = (c: WebCurrency) => {
    setCurrency(c);
    try {
      localStorage.setItem(CURRENCY_KEY, c);
    } catch {
      // ignore
    }
  };

  const go = async (id: string, url: () => Promise<{ url: string }>) => {
    if (!session) return navigate(`/login?next=${encodeURIComponent(wanted ? `/shop?product=${wanted}` : '/shop')}`);
    setBusy(id);
    setError(null);
    try {
      window.location.assign((await url()).url);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.generic'));
      setBusy(null);
    }
  };
  const off = catalog.data?.gift && useGift ? catalog.data.gift.percentOff : 0;
  const buy = (item: WebCatalogItem) => void go(item.productId, () => api.checkout(item.productId, cur, off > 0));
  const changePlan = () => void go('portal', api.portal);

  const items = catalog.data?.items ?? [];
  const of = (...kinds: WebCatalogItem['kind'][]) => items.filter((i) => kinds.includes(i.kind));
  const plans = of('subscription');
  const passes = of('pass');
  const packs = of('credits');
  const wanted = PRODUCT_ID.test(params.get('product') ?? '') ? params.get('product') : null;
  // Once the catalogue is in: bring the product the app asked for into view (once).
  const scrolled = useRef(false);
  useEffect(() => {
    if (!wanted || !catalog.data || scrolled.current) return;
    const el = document.getElementById(anchorOf(wanted));
    if (!el) return;
    scrolled.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [wanted, catalog.data]);

  return (
    <div className="shop">
      <div className="shop-bg" aria-hidden />
      <Link to="/" className="shop-logo" aria-label={t('nav.home')}>
        <Wordmark className="on-paper" />
        <img className="on-ink" src="/art/wordmark_white.webp" alt="" width={640} height={257} />
      </Link>
      <nav className="shop-side">
        <Link to="/">
          <HomeGlyph />
          {t('nav.home')}
        </Link>
        <Link to="/shop" className="active">
          <BagGlyph />
          {t('nav.shop')}
        </Link>
        <Link to={session ? '/account' : '/login?next=/shop'}>
          <UserGlyph />
          {t(session ? 'nav.account' : 'nav.login')}
        </Link>
        <LangToggle />
        <CurrencyPicker className="side-currency" value={cur} onChange={choose} compact />
      </nav>
      <div className="shop-grid">
        <div className="shop-main">
          <div className="shop-head">
            <h1>{t('shop.title')}</h1>
            {wallet.data ? (
              <span className="balance-chip">
                {t('shop.balance')}
                <CreditIcon size={30} />
                <b>{wallet.data.credits}</b>
              </span>
            ) : null}
            {!session ? (
              <Link to="/login?next=/shop" className="notice head-note">
                {t('shop.loginFirst')}
              </Link>
            ) : null}
          </div>
          {params.get('cancelled') ? <p className="notice warn" style={{ marginBottom: 16 }}>{t('shop.cancelled')}</p> : null}
          {error ? (
            <p className="error" role="alert" style={{ marginBottom: 16 }}>
              {error}
            </p>
          ) : null}
          {catalog.data?.gift ? (
            <label className="notice gift">
              <input type="checkbox" checked={useGift} onChange={(e) => setUseGift(e.target.checked)} />
              <span>
                <b>{t('shop.gift.title', { n: catalog.data.gift.percentOff })}</b>
                <small>{t('shop.gift.body')}</small>
              </span>
            </label>
          ) : null}
          {catalog.loading && !catalog.data ? <p className="muted">{t('loading')}</p> : null}
          {catalog.error ? <p className="error">{t('error.generic')}</p> : null}
          {plans.length ? (
            <Section title={t('shop.plus')} crown>
              <div className="grid grid-3">
                {plans.map((p) => (
                  <Plan key={p.productId} item={p} off={off} picked={wanted === p.productId} plus={wallet.data?.plus ?? null} busy={busy} onBuy={() => buy(p)} onChange={changePlan} />
                ))}
              </div>
            </Section>
          ) : null}
          {passes.length ? (
            <Section title={t('shop.other')}>
              <div className="grid grid-3">
                {passes.map((p) => (
                  <Bundle key={p.productId} item={p} off={off} picked={wanted === p.productId} busy={busy === p.productId} onBuy={() => buy(p)} />
                ))}
              </div>
            </Section>
          ) : null}
          {packs.length ? (
            <Section title={t('shop.credits')}>
              <div className="grid grid-3">
                {packs.map((p) => (
                  <Pack key={p.productId} item={p} off={off} picked={wanted === p.productId} busy={busy === p.productId} onBuy={() => buy(p)} />
                ))}
              </div>
              <p className="muted" style={{ marginTop: 12 }}>
                {t('shop.creditsNote')}
              </p>
            </Section>
          ) : null}
          <p className="secure">
            <LockGlyph />
            {t('shop.secure')}
          </p>
          <CurrencyPicker className="shop-tools" value={cur} onChange={choose} />
        </div>
        <div className="shop-cast-wrap">
          <img className="shop-cast" src="/art/group.webp" alt="" width={620} height={980} />
        </div>
      </div>
    </div>
  );
}

/** A shop section under its ink plate, or under a row of tabs whose open tab is drawn as the plate. */
function Section({ title, crown, heading, children }: { title?: string; crown?: boolean; heading?: ReactNode; children: ReactNode }) {
  return (
    <section className="section">
      <div className="section-title">
        {heading ?? (
          <Plate>
            {crown ? <CrownMark /> : <CreditIcon size={30} />}
            {title}
          </Plate>
        )}
      </div>
      {children}
    </section>
  );
}

const CrownMark = () => (
  <svg width="30" height="24" viewBox="0 0 32 26" aria-hidden style={{ verticalAlign: -3, marginRight: 10 }}>
    <path d="M2 6l7 7 7-11 7 11 7-7-3 15H5z" fill="#fff" />
  </svg>
);

const Badge = ({ item }: { item: WebCatalogItem }) => {
  const { t } = useI18n();
  return item.badge ? <span className="badge">{t(`badge.${item.badge}` as Key)}</span> : null;
};

/** The price, or with the gift ticked the price after it, the full one struck through (a subscription's first month only). */
function PriceText({ item, off = 0 }: { item: WebCatalogItem; off?: number }) {
  const { t, lang } = useI18n();
  if (item.amount === null) return <>{t('shop.unavailable')}</>;
  return (
    <>
      {off ? <s className="was">{formatPrice(item.amount, item.currency, lang)}</s> : null}
      {formatPrice(off ? Math.round((item.amount * (100 - off)) / 100) : item.amount, item.currency, lang)}
      {item.period ? <small>{t(`shop.per.${item.period}` as Key)}</small> : null}
    </>
  );
}

/**
 * A Plus plan with the concept's two or three short lines; the full list opens under 了解更多.
 * While Plus runs, the running plan reads 目前方案 and the others 更改方案, which opens Stripe's
 * billing portal (a plan bought in the app store is changed there instead).
 */
function Plan({ item, off, picked, plus, busy, onBuy, onChange }: { item: WebCatalogItem; off: number; picked: boolean; plus: WalletView['plus'] | null; busy: string | null; onBuy: () => void; onChange: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const running = !!plus?.active;
  const current = running && item.owned;
  const tier = item.tier === 'premium' ? 'premium' : 'plus';
  const short: { icon: ReactNode; text: string }[] = [
    { icon: <CreditIcon size={24} />, text: t('plan.short.tickets', { n: item.credits }) },
    { icon: <BookGlyph />, text: t('plan.short.coins', { n: item.dailyCoins }) },
    { icon: <MicGlyph />, text: t('plan.short.chats', { n: MEMBERSHIP.dailyNpcMessages[tier], c: MEMBERSHIP.dailyAiChapters[tier] }) },
  ];
  return (
    <article id={anchorOf(item.productId)} className={`card plan${item.badge === 'popular' ? ' hot' : ''}${picked ? ' picked' : ''}`}>
      <Badge item={item} />
      <h3>{t(`plan.${tier}`)}</h3>
      <div className={`price${item.amount === null ? ' na' : ''}`}>
        <PriceText item={item} off={off} />
      </div>
      {off && item.amount !== null ? <p className="first-month">{t('shop.gift.firstMonth')}</p> : null}
      <hr />
      <ul className="perks">
        {short.map((p) => (
          <li key={p.text}>
            {p.icon}
            {p.text}
          </li>
        ))}
      </ul>
      <button className="more" aria-expanded={open} onClick={() => setOpen(!open)}>
        {t(open ? 'shop.less' : 'shop.more')}
      </button>
      {open ? (
        <ul className="perks full">
          {item.perks.map((p, i) => (
            <li key={p}>
              {PERK_ICONS[i] ?? <BrushGlyph size={16} />}
              {p}
            </li>
          ))}
        </ul>
      ) : null}
      <span className="spacer" />
      {current ? (
        <button className="btn btn-ink btn-full" disabled>
          {t('shop.currentPlan')}
        </button>
      ) : running ? (
        <button className="btn btn-pink btn-full" disabled={plus?.source !== 'stripe' || busy === 'portal'} onClick={onChange}>
          {t('shop.changePlan')}
        </button>
      ) : (
        <button className="btn btn-pink btn-full" disabled={item.amount === null || !item.eligible || busy === item.productId} onClick={onBuy}>
          {busy === item.productId ? '…' : t('shop.subscribe')}
        </button>
      )}
    </article>
  );
}

/** The currency the prices show in: in the sidebar on wide screens, above the extra packs on narrow ones. */
function CurrencyPicker({ className, value, onChange, compact }: { className: string; value: WebCurrency; onChange: (c: WebCurrency) => void; compact?: boolean }) {
  const { t, lang } = useI18n();
  return (
    <label className={className}>
      {t('shop.currency')}
      <select className="select" value={value} onChange={(e) => onChange(e.target.value as WebCurrency)}>
        {WEB_CURRENCIES.map((c) => (
          <option key={c} value={c}>
            {compact && lang === 'en' ? c.toUpperCase() : CURRENCY_LABEL[c][lang === 'en' ? 'en' : 'zh']}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A pack of chapter tickets: how many chapters big, its price, and what each chapter comes to. */
function Pack({ item, off, picked, busy, onBuy }: { item: WebCatalogItem; off: number; picked: boolean; busy: boolean; onBuy: () => void }) {
  const { t, lang } = useI18n();
  return (
    <article id={anchorOf(item.productId)} className={`card pack${picked ? ' picked' : ''}`}>
      <Badge item={item} />
      <img className="art" src={`/art/${PACK_ART[item.productId] ?? 'coin_1'}.webp`} alt="" width={104} height={92} />
      <div>
        <div className="qty">
          {item.credits}
          <small>{t('shop.unit.credits')}</small>
        </div>
        <div className={`price${item.amount === null ? ' na' : ''}`}>
          <PriceText item={item} off={off} />
        </div>
        <button className="btn btn-pink" disabled={item.amount === null || !item.eligible || item.owned || busy} onClick={onBuy}>
          {busy ? '…' : item.owned ? t('shop.owned') : t('shop.buy')}
        </button>
      </div>
      {item.credits > 1 && item.amount !== null ? <span className="note">{t('shop.perChapter', { price: formatPrice(Math.round(item.amount / item.credits / 10) * 10, item.currency, lang) })}</span> : null}
    </article>
  );
}

function Bundle({ item, off, picked, busy, onBuy }: { item: WebCatalogItem; off: number; picked: boolean; busy: boolean; onBuy: () => void }) {
  const { t } = useI18n();
  return (
    <article id={anchorOf(item.productId)} className={`card bundle${picked ? ' picked' : ''}`}>
      <Badge item={item} />
      <div className="head">
        <img src={`/art/${PACK_ART[item.productId] ?? 'coin_3'}.webp`} alt="" width={72} height={64} />
        <div>
          <h3>{item.title}</h3>
          <div className={`price${item.amount === null ? ' na' : ''}`}>
            <PriceText item={item} off={off} />
          </div>
        </div>
      </div>
      <ul className="perks">
        {(item.perks.length ? item.perks : [item.description]).map((p) => (
          <li key={p}>
            <CheckGlyph size={18} />
            {p}
          </li>
        ))}
      </ul>
      <button className="btn btn-pink btn-full" disabled={item.amount === null || !item.eligible || item.owned || busy} onClick={onBuy}>
        {busy ? '…' : item.owned ? t('shop.owned') : t('shop.buy')}
      </button>
    </article>
  );
}
